import React, { useEffect, useState } from 'react';
import {
  getRegistrations, acceptRegistration, rejectRegistration,
  getAllContent, saveContent, publishContent,
  getTeams, Registration, Team,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import '../components/TeamNav.css';
import './Registrations.css';

/**
 * Registrations — the queue and the form that feeds it, in one place.
 *
 * The form settings live here rather than in the Content Editor because this is
 * where someone thinks about registration. Splitting "who has signed up" from
 * "what we ask them" meant nobody found the second one.
 *
 * Only guardian-confirmed rows reach the queue; unconfirmed submissions are
 * withheld by the API and delete themselves, so this never becomes a spam bin.
 */

const VISIBILITY_SECTION = 'join-form-visibility';
const CUSTOM_SECTION = 'join-form-custom';     // legacy: 5 flat labels, migrated on first load
const FIELDS_SECTION = 'join-form-fields';

type FieldType = 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'number';
interface RegField {
  id: string; type: FieldType; label: string;
  help?: string; required?: boolean; options?: string[];
}

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short answer',
  textarea: 'Long answer',
  select: 'Choose from a list',
  checkbox: 'Tick box (yes / no)',
  date: 'Date',
  number: 'Number',
};

// Starting points, not a menu of everything. These are the questions grassroots
// clubs actually ask, so the common case is one click rather than typing.
const PRESETS: Array<{ label: string; field: Omit<RegField, 'id'> }> = [
  // No medical/allergy preset. Health answers are Article 9 special category
  // data — they need an Art.9 condition on top of a lawful basis, and often an
  // appropriate policy document. Offering it as a one-click suggestion pushed
  // clubs into that obligation without them realising. A club that genuinely
  // needs it can still write the question itself; we just stop proposing it.
  { label: 'Emergency contact',
    field: { type: 'text', label: 'Emergency contact name and number', required: true } },
  { label: 'Photo consent',
    field: { type: 'checkbox', label: 'I consent to photographs of my child being used on the club website and social media', help: 'You can withdraw this at any time.' } },
  { label: 'Preferred foot',
    field: { type: 'select', label: 'Preferred foot', options: ['Left', 'Right', 'Both'] } },
  { label: 'Previous club',
    field: { type: 'text', label: 'Previous club (if any)' } },
  { label: 'School',
    field: { type: 'text', label: 'School' } },
  { label: 'How did you hear about us?',
    field: { type: 'text', label: 'How did you hear about us?' } },
];

const newId = () => `q-${Math.random().toString(36).slice(2, 9)}`;

/** Move one question up or down. Written out rather than destructured so it
 *  survives noUncheckedIndexedAccess. */
function swap(list: RegField[], a: number, b: number): RegField[] {
  if (a < 0 || b < 0 || a >= list.length || b >= list.length) return list;
  const next = [...list];
  const tmp = next[a] as RegField;
  next[a] = next[b] as RegField;
  next[b] = tmp;
  return next;
}

const TOGGLES = [
  { key: 'showPhone', label: 'Phone number', hint: 'A second way to reach the guardian.' },
  { key: 'showAgeGroup', label: 'Preferred age group', hint: 'What the guardian thinks fits. You still choose the team.' },
  { key: 'showExperience', label: 'Football experience', hint: 'Optional for the guardian to answer.' },
  { key: 'showMessage', label: 'Additional information', hint: 'Free text box at the end of the form.' },
];

const Registrations: React.FC = () => {
  const { clubId } = useAuth() as any;
  const [tab, setTab] = useState<'queue' | 'form'>('queue');

  const [rows, setRows] = useState<Registration[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string>('');
  const [chosenTeam, setChosenTeam] = useState<Record<string, string>>({});

  const [visibility, setVisibility] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<RegField[]>([]);
  const [savingForm, setSavingForm] = useState(false);
  const [formSaved, setFormSaved] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([getRegistrations(), getTeams().catch(() => [])]);
      setRows(r);
      setTeams(t as Team[]);
      setChosenTeam(prev => {
        const next = { ...prev };
        for (const reg of r) if (reg.teamId && !next[reg.registrationId]) next[reg.registrationId] = reg.teamId;
        return next;
      });
      setError('');
      window.dispatchEvent(new Event('registrations-changed'));
    } catch (e: any) {
      setError(e?.message || 'Could not load registrations.');
    } finally {
      setLoading(false);
    }
  };

  const loadFormSettings = async () => {
    try {
      // There is NO GET /admin/content/{section} route — only the bulk one.
      // Fetching per-section 404'd, the catch swallowed it, and the builder
      // came back empty every time even though the save had worked.
      const all = await getAllContent('draft');
      const bySection = (id: string) =>
        (all.find(c => c.section === id)?.content || {}) as Record<string, unknown>;

      const vc = bySection(VISIBILITY_SECTION);
      setVisibility(Object.fromEntries(TOGGLES.map(t => [t.key, String(vc[t.key] ?? 'yes')])));
      // Prefer the builder's own store. If it is empty, carry over the five
      // legacy text questions so a club that had them does not lose them.
      const rawFields = bySection(FIELDS_SECTION).fieldsJson;
      let parsed: RegField[] = [];
      try { parsed = typeof rawFields === 'string' ? JSON.parse(rawFields) : (rawFields || []); } catch { parsed = []; }
      if (Array.isArray(parsed) && parsed.length) {
        setFields(parsed);
      } else {
        const cc = bySection(CUSTOM_SECTION);
        setFields([1, 2, 3, 4, 5]
          .map(n => String(cc[`custom${n}Label`] ?? '').trim())
          .filter(Boolean)
          .map(label => ({ id: newId(), type: 'text' as FieldType, label })));
      }
    } catch { /* defaults are fine — the form works without saved content */ }
  };

  useEffect(() => { load(); loadFormSettings(); }, []);

  const onAccept = async (r: Registration) => {
    setBusy(r.registrationId);
    try {
      await acceptRegistration(r.registrationId, chosenTeam[r.registrationId]);
      await load();
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not accept this registration.');
    } finally { setBusy(''); }
  };

  const onReject = async (r: Registration) => {
    const who = `${r.playerFirstName} ${r.playerLastName || ''}`.trim();
    if (!window.confirm(`Reject the registration for ${who}? Their details are deleted after 30 days.`)) return;
    setBusy(r.registrationId);
    try {
      await rejectRegistration(r.registrationId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not reject this registration.');
    } finally { setBusy(''); }
  };

  const saveForm = async () => {
    setSavingForm(true);
    setFormSaved('');
    try {
      // Save AND publish — an admin changing their form here expects the public
      // page to change, not to go hunting for a Publish button somewhere else.
      await saveContent(VISIBILITY_SECTION, visibility);
      await publishContent(VISIBILITY_SECTION);
      await saveContent(FIELDS_SECTION, { fieldsJson: JSON.stringify(fields) });
      await publishContent(FIELDS_SECTION);
      setFormSaved('Saved — your Join page is updated.');
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not save the form settings.');
    } finally { setSavingForm(false); }
  };

  const awaiting = rows.filter(r => r.status === 'verified');
  const decided = rows.filter(r => r.status !== 'verified');

  const age = (dob?: string) => {
    if (!dob) return '';
    const d = new Date(`${dob}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    return `${Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000))}`;
  };

  return (
    <div className="tm-page-shell">
      <div className="page-head">
        <div>
          <h1>Registrations</h1>
          <p className="subtitle">
            Sign-ups from your Join page, and the questions that form asks.
            Sign-ups nobody has confirmed by email are never shown here and delete
            themselves after 72 hours.
          </p>
        </div>
        <div className="head-actions">
          {clubId && (
            <a
              className="ebtn ebtn-secondary"
              href={`https://${clubId}.grassrootsfc.co.uk/join`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View live form ↗
            </a>
          )}
          {tab === 'queue' && (
            <button className="ebtn ebtn-secondary" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
        </div>
      </div>

      <nav className="tm-nav" aria-label="Registration sections">
        <div className="tm-nav-tabs">
          <button className={`tm-nav-tab ${tab === 'queue' ? 'active' : ''}`} onClick={() => setTab('queue')}>
            Awaiting decision{awaiting.length ? ` (${awaiting.length})` : ''}
          </button>
          <button className={`tm-nav-tab ${tab === 'form' ? 'active' : ''}`} onClick={() => setTab('form')}>
            Form setup
          </button>
        </div>
      </nav>

      {error && <div className="ealert ealert-error">{error}</div>}

      {tab === 'queue' && (
        <>
          {loading && <div className="empty-state"><p>Loading…</p></div>}

          {!loading && awaiting.length === 0 && (
            <div className="empty-state">
              <p><strong>Nothing waiting for a decision.</strong></p>
              <p className="tm-muted">
                New sign-ups appear here once the parent or guardian has clicked the
                confirmation link in their email.
              </p>
            </div>
          )}

          {awaiting.map(r => {
            const yrs = age(r.dateOfBirth);
            const picked = chosenTeam[r.registrationId];
            return (
              <div key={r.registrationId} className="card-surface reg-card">
                <div>
                  <h3 className="reg-name">{r.playerFirstName} {r.playerLastName || ''}</h3>
                  <p className="reg-born">
                    {r.dateOfBirth
                      ? <>Born {r.dateOfBirth}{yrs ? <> · age {yrs}</> : null}</>
                      : 'No date of birth given'}
                  </p>

                  <p className="reg-detail">
                    <span className="reg-detail-label">Parent / guardian</span>
                    {r.guardianName}<br />
                    <a href={`mailto:${r.guardianEmail}`}>{r.guardianEmail}</a>
                    {r.guardianPhone ? <><br />{r.guardianPhone}</> : null}
                  </p>

                  {Array.isArray((r as any).answers) && (r as any).answers.length > 0 && (
                    <dl className="reg-answers">
                      {(r as any).answers.map((a: any) => (
                        <React.Fragment key={a.id}>
                          <dt>{a.label}</dt>
                          <dd>{a.value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}

                  {r.notes && <p className="reg-notes">{r.notes}</p>}

                  {r.verifiedAt && (
                    <span className="reg-confirmed">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Confirmed by email {new Date(r.verifiedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <div className="reg-actions">
                  <div>
                    <label htmlFor={`team-${r.registrationId}`}>Add to team</label>
                    <select
                      id={`team-${r.registrationId}`}
                      value={picked || ''}
                      onChange={e => setChosenTeam(p => ({ ...p, [r.registrationId]: e.target.value }))}
                    >
                      <option value="">Choose a team…</option>
                      {teams.map(t => (
                        <option key={(t as any).id} value={(t as any).id}>
                          {t.name}{(t as any).ageGroup ? ` (${(t as any).ageGroup})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className="ebtn ebtn-primary"
                    disabled={busy === r.registrationId || !picked}
                    onClick={() => onAccept(r)}
                    title={!picked ? 'Choose a team first' : undefined}
                  >
                    {busy === r.registrationId ? 'Working…' : 'Accept — create player'}
                  </button>
                  <button
                    className="ebtn ebtn-danger"
                    disabled={busy === r.registrationId}
                    onClick={() => onReject(r)}
                  >
                    Reject
                  </button>
                  <p className="reg-hint">
                    {picked
                      ? 'Accepting adds them to your squad and counts towards your plan.'
                      : 'Choose a team to enable Accept — a player with no team never appears on your squad pages.'}
                  </p>
                </div>
              </div>
            );
          })}

          {decided.length > 0 && (
            <>
              <h2 className="reg-section-title">Already dealt with</h2>
              <div className="card-surface">
                <table className="tm-table">
                  <thead>
                    <tr><th>Player</th><th>Parent / guardian</th><th>Outcome</th></tr>
                  </thead>
                  <tbody>
                    {decided.map(r => (
                      <tr key={r.registrationId}>
                        <td>{r.playerFirstName} {r.playerLastName || ''}</td>
                        <td>{r.guardianName}</td>
                        <td>
                          <span className={`reg-status reg-status-${r.status}`}>
                            {r.status === 'accepted' ? 'Accepted' : 'Rejected'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'form' && (
        <>
          <div className="card-surface reg-form-panel">
            <h2 className="reg-panel-title">Always asked</h2>
            <p className="reg-panel-sub">
              These four make a sign-up into a player record and record the guardian's
              consent, so they can't be turned off.
            </p>
            <ul className="reg-locked-list">
              <li>Player first name</li>
              <li>Date of birth</li>
              <li>Parent / guardian name</li>
              <li>Parent / guardian email</li>
            </ul>
          </div>

          <div className="card-surface reg-form-panel">
            <h2 className="reg-panel-title">Optional questions</h2>
            <p className="reg-panel-sub">Turn off anything you don't need. Hidden questions vanish from the form entirely.</p>
            {TOGGLES.map(t => (
              <label key={t.key} className="reg-toggle">
                <input
                  type="checkbox"
                  checked={visibility[t.key] !== 'no'}
                  onChange={e => setVisibility(v => ({ ...v, [t.key]: e.target.checked ? 'yes' : 'no' }))}
                />
                <span>
                  <strong>{t.label}</strong>
                  <em>{t.hint}</em>
                </span>
              </label>
            ))}
          </div>

          <div className="card-surface reg-form-panel">
            <h2 className="reg-panel-title">Your own questions</h2>
            <p className="reg-panel-sub">
              Anything else you need from a parent. Answers arrive with the registration
              and are stored with the question exactly as it was worded, so editing a
              question later never rewrites what someone already agreed to.
            </p>

            {fields.length === 0 && (
              <p className="reg-empty-fields">No extra questions yet — add one below.</p>
            )}

            {fields.map((f, i) => (
              <div key={f.id} className="reg-field">
                <div className="reg-field-head">
                  <span className="reg-field-num">{i + 1}</span>
                  <select
                    value={f.type}
                    aria-label="Question type"
                    onChange={e => setFields(list => list.map((x, j) => j === i
                      ? { ...x, type: e.target.value as FieldType,
                          options: e.target.value === 'select' ? (x.options?.length ? x.options : ['Option 1']) : undefined }
                      : x))}
                  >
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <div className="reg-field-tools">
                    <button className="ebtn ebtn-ghost ebtn-sm" disabled={i === 0}
                      title="Move up"
                      onClick={() => setFields(l => swap(l, i, i - 1))}>↑</button>
                    <button className="ebtn ebtn-ghost ebtn-sm" disabled={i === fields.length - 1}
                      title="Move down"
                      onClick={() => setFields(l => swap(l, i, i + 1))}>↓</button>
                    <button className="ebtn ebtn-ghost ebtn-sm reg-remove"
                      title="Remove question"
                      onClick={() => setFields(l => l.filter((_, j) => j !== i))}>Remove</button>
                  </div>
                </div>

                <input
                  className="reg-field-label"
                  placeholder="Type the question as a parent will read it"
                  value={f.label}
                  onChange={e => setFields(l => l.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                />
                <input
                  className="reg-field-help"
                  placeholder="Help text under the question (optional)"
                  value={f.help || ''}
                  onChange={e => setFields(l => l.map((x, j) => j === i ? { ...x, help: e.target.value } : x))}
                />

                {f.type === 'select' && (
                  <div className="reg-field-options">
                    <label>Choices — one per line</label>
                    <textarea
                      rows={Math.max(2, (f.options || []).length)}
                      value={(f.options || []).join('\n')}
                      onChange={e => setFields(l => l.map((x, j) => j === i
                        ? { ...x, options: e.target.value.split('\n').map(o => o.trim()).filter(Boolean) } : x))}
                    />
                  </div>
                )}

                <label className="reg-field-required">
                  <input type="checkbox" checked={f.required === true}
                    onChange={e => setFields(l => l.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))} />
                  <span>Required — a parent can't submit without answering</span>
                </label>
              </div>
            ))}

            <div className="reg-add">
              <button className="ebtn ebtn-secondary"
                onClick={() => setFields(l => [...l, { id: newId(), type: 'text', label: '' }])}>
                + Add a question
              </button>
              <span className="reg-add-or">or start from</span>
              {PRESETS.map(p => (
                <button key={p.label} className="ebtn ebtn-ghost ebtn-sm"
                  onClick={() => setFields(l => [...l, { id: newId(), ...p.field }])}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="reg-save-bar">
            <button className="ebtn ebtn-primary" onClick={saveForm} disabled={savingForm}>
              {savingForm ? 'Saving…' : 'Save form'}
            </button>
            {formSaved && <span className="reg-saved">{formSaved}</span>}
          </div>
        </>
      )}
    </div>
  );
};

export default Registrations;
